// vue router

import Vue from 'vue';
import VueRouter from 'vue-router';

import BasicTemplate from 'js/components/BasicTemplate.vue';
import WorkspaceManager from 'js/components/WorkspaceManager.vue';
import GroupManager from 'js/components/GroupManager.vue';


import Login from 'js/components/auth/Login.vue';
import Logout from 'js/components/auth/Logout.vue';
import Register from 'js/components/auth/Register.vue';
import RegisterVerify from 'js/components/auth/RegisterVerify.vue';
import ForgotPassword from 'js/components/auth/ForgotPassword.vue';
import ResetPassword from 'js/components/auth/ResetPassword.vue';
import EmailConfirm from 'js/components/settings/EmailConfirm.vue';

import Period from 'js/components/Period.vue';
import TermOverview from 'js/components/overview/TermOverview.vue';

import Assignment from 'js/components/assignment/Assignment.vue';
import AssignmentStep from 'js/components/assignment/Step.vue';
import AssignmentDone from 'js/components/assignment/StepDone.vue';
import AssignmentOverview from 'js/components/assignment/StepOverview.vue';
import AssignmentGroup from 'js/components/assignment/StepGroup.vue';

import Groups from 'js/components/groups/Groups.vue';
import Students from 'js/components/students/Students.vue';

import Lobby from 'js/components/Lobby.vue';
import NotFound from 'js/components/NotFound.vue';

import { canTeach, checkAuthentication, checkAuthorization } from 'js/api/auth';
import { fetchUser } from 'js/vuex/actions';

import track from 'js/helpers/analytics';

Vue.use(VueRouter);

function prefix(route) {
    if ( route != null && route.params.groupId != null ) {
        return route.params.studentId != null ? 'students' : 'group'
    }
    return 'student'
}

function autoRedirect(name) {
    return to => {
        const current = router.history.current
        const next = { name: prefix(current) + '.' + name, params: to.params, query: to.query }
        return router.match(next, current)
    }
}


const routes = [

    {
        path: '/',
        name: 'root',
        meta: {
            auth: true
        },
        beforeEnter: function(to, from, next) {
            const user = router.app.$store.state.users.current
            if (canTeach(user)) {
                return next({ name: 'groups' })
            }

            if (user.groups != null && user.groups.length > 0) {
                return next({ name: 'student.period' })
            }

            return next({ name: 'lobby' })
        },
    } ,{
        path: '/login',
        name: 'login',
        component: Login,
    }, {
        path: '/logout',
        name: 'logout',
        component: Logout,
    }, {
        path: '/register',
        name: 'register',
        component: Register,
    }, {
        path: '/register/verify/:token(.*)',
        name: 'register.verify',
        component: RegisterVerify,
    }, {
        path: '/password/forgot',
        name: 'forgot',
        component: ForgotPassword,
    }, {
        path: '/password/reset/:resetToken',
        name: 'reset',
        component: ResetPassword,
    }, {
        path: '/email/confirm/:token',
        name: 'email.confirm',
        component: EmailConfirm,
    },


    // automatic redirects based on current state
    {
        path: '/r',
        children: [
            {
                path: 'period/:periodId(\\d+)?',
                name: 'period',
                redirect: autoRedirect('period')
            }, {
                path: 'assignment/:assignmentId(\\d+)',
                name: 'assignment',
                redirect: autoRedirect('assignment'),
                children: [{
                    path: 'done',
                    name: 'assignment.done',
                    redirect: autoRedirect('assignment.done'),
                }, {
                    path: 'overview',
                    name: 'assignment.overview',
                    redirect: autoRedirect('assignment.overview'),
                }, {
                    path: 'group',
                    name: 'assignment.group',
                    redirect: autoRedirect('assignment.group'),
                }, {
                    path: ':stepIdx',
                    name: 'assignment.step',
                    redirect: autoRedirect('assignment.step'),
                }]
            }

        ]

    },


    {
        // Students
        path: '/workspace',
        component: WorkspaceManager,
        redirect: { name: 'root' },
        meta: {
            auth: true
        },

        beforeEnter: function(to, from, next) {
            const user = router.app.$store.state.users.current
            return (canTeach(user) || user.groups == null || user.groups.length === 0)
              ? next({ name: 'root' })
              : next()
        },

        children: [
            {
                path: '/overview',
                name: 'student.overview',
                component: TermOverview,
                meta: {
                    breadcrumb: 'Overzicht'
                }
            },

            {
                path: '/period/:periodId(\\d+)?',
                name: 'student.period',
                component: Period,
                meta: {
                    breadcrumb: 'Opdrachten'
                }
            }, {
                path: '/assignment/:assignmentId(\\d+)',
                name: 'student.assignment',
                component: Assignment,
                props: true,
                children: [{
                    path: 'done',
                    name: 'student.assignment.done',
                    component: AssignmentDone,
                }, {
                    path: 'overview',
                    name: 'student.assignment.overview',
                    component: AssignmentOverview,
                }, {
                    path: 'group',
                    name: 'student.assignment.group',
                    component: AssignmentGroup,
                }, {
                    path: ':stepIdx',
                    name: 'student.assignment.step',
                    component: AssignmentStep,
                }]
            },

        ]
    },


    {
        // Teachers
        path: '/group/:groupId(\\d+)',
        component: GroupManager,
        redirect: { name: 'root' },
        meta: {
            auth: true
        },
        children: [
            {
                path: 'overview',
                name: 'group.overview',
                component: TermOverview,
                meta: {
                    breadcrumb: 'Overzicht'
                }
            },

            {
                path: ':groupTermId(\\d+)?',
                name: 'students.overview',
                props: true,
                component: Students,
                meta: {
                    breadcrumb: 'Voortgang'
                }
            },

            {
                path: 'period/:periodId(\\d+)',
                name: 'group.period',
                component: Period,
                meta: {
                    breadcrumb: 'Blok'
                }
            }, {
                path: 'period/:periodId(\\d+)/assignment/:assignmentId(\\d+)',
                name: 'group.assignment',
                component: Assignment,
                props: true,
                children: [{
                    path: 'done',
                    name: 'group.assignment.done',
                    component: AssignmentGroup,
                }, {
                    path: 'overview',
                    name: 'group.assignment.overview',
                    redirect: { name: 'group.assignment.done' }
                }, {
                    path: 'group',
                    name: 'group.assignment.group',
                    redirect: { name: 'group.assignment.done' }
                }, {
                    path: ':stepIdx',
                    name: 'group.assignment.step',
                    component: AssignmentStep,
                }]

            },

            {
                path: 'student/:studentId(\\d+)/period/:periodId(\\d+)?',
                name: 'students.period',
                component: Period,
                meta: {
                    breadcrumb: 'Opdrachten leerling'
                }
            }, {
                path: 'student/:studentId(\\d+)/assignment/:assignmentId(\\d+)',
                name: 'students.assignment',
                component: Assignment,
                props: true,
                children: [{
                    path: 'done',
                    name: 'students.assignment.done',
                    redirect: { name: 'students.assignment.overview' }
                }, {
                    path: 'overview',
                    name: 'students.assignment.overview',
                    component: AssignmentOverview,
                }, {
                    path: 'group',
                    name: 'students.assignment.group',
                    component: AssignmentGroup,
                }, {
                    path: ':stepIdx',
                    name: 'students.assignment.step',
                    component: AssignmentStep,
                }]
            },

        ]

    }, {
        path: '/basictemplate',
        component: BasicTemplate,
        name: 'template',
        redirect: { name: 'root' },
        meta: {
            auth: true,
        },
        children: [{
            path: '/groups',
            name: 'groups',
            component: Groups
        },         ]

    }, {
        path: '/lobby',
        name: 'lobby',
        component: Lobby,
        meta: {
            auth: true,
        },
        beforeEnter: function(to, from, next) {
            const user = router.app.$store.state.users.current
            return (canTeach(user) || (user.groups != null && user.groups.length > 0))
              ? next({ name: 'root' })
              : next()
        },
    }, {
        path: '*',
        name: 'not-found',
        component: NotFound,
        beforeEnter: (to, from, next) => {
            track.event( 'router', '404', to.path + '[' + from.path +']')
            next()
        }
    }
]

const router = new VueRouter({
    base: '/app',
    mode: 'history',
    routes,
    scrollBehavior (to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition
        } else {
            return { x: 0, y: 0 }
        }
    }
});


/**
 * Breadcrumbs return
 */
router.beforeEach((to, from, next) => {
    if (to.meta && to.meta.breadcrumb != null) {
        router.app.$store.dispatch('SET_BREADCRUMB', { route: to.fullPath, name: to.meta.breadcrumb })
    }
    next()
})

/**
 * Analytics tracking
 */
router.beforeEach((to, from, next) => {
    console.log('>>> navigate', to)
    track.pageview(to.path)
    next()
})


/**
 * Authentication check
 * User must be authenticated before he can access
 * routes that have auth activated
 */
router.beforeEach((to, from, next) => {

    if ( to.matched.some(record => record.meta.auth) ) {

        // called when we need to actually fetch the user, this triggers loading indicator in Main.vue
        const beforeFetchHook = () => router.app.$store.dispatch('LOADING_USER')

        checkAuthentication()
            .then( id => { track.user(id); return id })
            .then( id => fetchUser( { dispatch: router.app.$store.dispatch, state: router.app.$store.state }, id, true, beforeFetchHook) )
            .then( user => checkAuthorization(to, user) )
            .then( authorized => {

                if ( !authorized ) {
                    console.error('Unauthorized')
                    return from ? next(false) : next({ name: 'root', replace: true })
                }

                next()

            }, error => {
                console.error('Authentication error', error)
                next( { name: 'logout', replace: true, params: { _internal: true } } )
            })

    } else {
        next()
    }

})

router.afterEach((to, from) => {
    if ( router.app.$store.state.loading.user ) {
        // called in afterEach to make sure the router view is actually changed
        // otherwise the previous router view could be rendered again, before switching to new view
        router.app.$store.dispatch('FINISHED_LOADING_USER')
    }
})

export default router



// WEBPACK FOOTER //
// ./resources/assets/js/router.js